// Authentication context: who is signed in, and how to sign in/out.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError } from './api'
import type { Role, User } from './types'

interface AuthContextValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User) => void
  refresh: () => Promise<void>
  hasRole: (min: Role) => boolean
}

const RANK: Record<Role, number> = { member: 0, supervisor: 1, admin: 2 }

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.me()
      setUserState(user)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setUserState(null)
    }
  }, [])

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string) => {
      // The login response only carries the session basics; re-read the full
      // profile (preferred language, display mode, supervisor) so those apply
      // immediately rather than only after the next reload.
      await api.login(email, password)
      await refresh()
    },
    [refresh],
  )

  const logout = useCallback(async () => {
    await api.logout()
    setUserState(null)
  }, [])

  const hasRole = useCallback(
    (min: Role) => (user ? RANK[user.role] >= RANK[min] : false),
    [user],
  )

  const value: AuthContextValue = {
    user,
    loading,
    login,
    logout,
    setUser: setUserState,
    refresh,
    hasRole,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
